'use client';

import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { useFragment } from '@apollo/client';
import { useStorageCell } from '@frameio/basics/react/useStorageCell';
import type { ShareFeatureFlagsFragment } from '@frameio/graphql-client/generated';
import { ShareFeatureFlags } from '@frameio/graphql-client/generated';
import {
  type GetFeatureFlagsQueryVariables,
  useGetFeatureFlagsQuery,
} from '../../gql/queries/generated/GetFeatureFlags';
import { useGetFeatureFlagsForSecureReviewerQuery } from '../../gql/queries/generated/GetFeatureFlagsForSecureReviewer';
import type { FeatureFlagName } from './flagNames';
import { flagNames } from './flagNames';

export type FeatureFlagState = {
  __typename?: 'FeatureFlag';
  name: FeatureFlagName;
  enabled: boolean;
  override?: boolean;
  originalValue?: boolean;
};

type FeatureFlagAppModeProps = {
  mode: 'app';
  shareId?: never;
};

type FeatureFlagShareModeProps = {
  mode: 'share-review';
  shareId: string;
};

type FeatureFlagSecureReviewerModeProps = {
  mode: 'secure-reviewer';
  shareId?: never;
};

export type FeatureFlagModeProps =
  | FeatureFlagAppModeProps
  | FeatureFlagShareModeProps
  | FeatureFlagSecureReviewerModeProps;

type FeatureFlagDefaultProps = React.PropsWithChildren<{
  accountId: string;
}>;

type FeatureFlagsContextProviderProps = FeatureFlagModeProps &
  FeatureFlagDefaultProps;

type FeatureFlagsContextProviderValue = {
  areFeatureFlagsLoading: boolean;
  getAllFeatureFlagStates: () => FeatureFlagState[];
  haveFeatureFlagsLoaded: boolean;
  isFeatureFlagEnabled: (flagName: FeatureFlagName) => boolean;
  updateOverrides: (
    update:
      | Record<FeatureFlagName, boolean>
      | ((
          oldValue: Record<FeatureFlagName, boolean>
        ) => Record<FeatureFlagName, boolean>)
  ) => void;
};

type FeatureFlagData = {
  featureFlags?:
    | ({ name?: string | null; enabled?: boolean | null } | null | undefined)[]
    | null;
};

type ModeDataResult = {
  data: FeatureFlagData | undefined;
  loading: boolean;
  error: unknown;
};

type FeatureFlagSources = {
  app: {
    data: { account?: FeatureFlagData | null } | undefined;
    loading: boolean;
    error: unknown;
  };
  share: { data: FeatureFlagData | undefined };
  secureReviewer: {
    data:
      | { me?: (FeatureFlagData & { id?: string }) | object | null }
      | undefined;
    loading: boolean;
    error: unknown;
  };
};

/**
 * Normalizes feature flag data from different sources based on the application mode.
 * Each mode (app, share-review, secure-reviewer) fetches feature flags differently,
 * so this function abstracts the varying data structures into a consistent format
 * for the FeatureFlagsContext to consume.
 */
const getModeData = (
  mode: FeatureFlagModeProps['mode'],
  sources: FeatureFlagSources
): ModeDataResult => {
  switch (mode) {
    case 'share-review':
      return { data: sources.share.data, loading: false, error: null };
    case 'secure-reviewer': {
      const me = sources.secureReviewer.data?.me;
      const data =
        me && 'featureFlags' in me ? (me as FeatureFlagData) : undefined;
      return {
        data,
        loading: sources.secureReviewer.loading,
        error: sources.secureReviewer.error,
      };
    }
    case 'app':
      return {
        data: sources.app.data?.account ?? undefined,
        loading: sources.app.loading,
        error: sources.app.error,
      };
    default:
      return {
        data: sources.app.data?.account ?? undefined,
        loading: sources.app.loading,
        error: sources.app.error,
      };
  }
};

export const FeatureFlagsContext =
  createContext<FeatureFlagsContextProviderValue>({
    areFeatureFlagsLoading: false,
    getAllFeatureFlagStates: () => [],
    haveFeatureFlagsLoaded: false,
    isFeatureFlagEnabled: (__: FeatureFlagName) => false,
    updateOverrides: () => {
      // Default implementation - does nothing
    },
  });
FeatureFlagsContext.displayName = 'FeatureFlagsContext';

/**
 * This Provider and associated hooks provide access to Feature Flags driven by
 * `massdriver` and the GraphQL API. These Feature Flags can be managed via
 * Support Tool, and can be toggled on for specific Users, Accounts, or rolled
 * out via a percentage.
 *
 * Flags are persisted according to environment. To create a new Feature Flag:
 *
 * 1. Go to https://support-tool.[env.]frame.io/flags
 * 2. Click "Create Flag"
 * 3. Optionally target Accounts, Users, or set a percentage
 * 4. Name your flag and save it by clicking "Create feature flag"
 *
 * Recommendation: to disambiguate legacy feature flags from new/v4 ones, use
 * a `v4.` prefix when naming your `web-app` or v4 specific flags!
 *
 * This Context Provider is built to serve Feature Flags in two different
 * modes using two different APIs:
 *
 * 1. In the main app, via `MainAppClientProviders`, with `account.featureFlags`
 * 2. In shares, via `ClientProviders`, with `share.featureFlags`
 *
 * Depending on the `mode` prop, the Provider will fetch the Feature Flags from
 * either `account.featureFlags` or `share.featureFlags` APIs.
 *
 * When the `mode` prop is set to "share-review", a `shareId` prop is required.
 * This mode is only intended for use on /shares routes, where the requesting
 * user may not have access to a Frame.io account. The flags query is resolved
 * server-side and hydrated client-side via the shares `layout.tsx`.
 *
 * In "app" mode, the `accountId is derived from the `useActiveAccountId` hook.
 * This mode will be used across all other routes, including the share builder,
 * and presumes an authenticated AccountUser.
 *
 * To use the Feature Flag in `web-app`:
 *
 * 1. Add your flag name to the `FEATURE_FLAG_NAMES` array in ./flagNames.ts
 * 2. Use the useFeatureFlagEnabled() method to check the flag state.
 * 3. Optionally use the useAreFeatureFlagsLoading() to check whether the
 *    underlying GraphQL query is still loading, and provide a graceful
 *    transition on the changing state.
 * 4. Or, if you need to switch between two *different* states depending on a
 *    flag state, and need to avoid a flash of the default state during initial
 *    load, use the useHaveFeatureFlagsLoaded() hook to check that the query
 *    has returned data. Note: this will reset to `false` when switching to a
 *    new Account that hasn't yet queried for its Feature Flags (until that
 *    newly active Account's flags states have been fetched).
 *
 * Example for basic usage:
 *
 * ```js
 * import { useFeatureFlagEnabled } from '@frameio/feature-flags';
 *
 * const isMyFlagEnabled = useFeatureFlagEnabled('v4.my_flag');
 * ```
 *
 * Example for conditionally rendering against the loading state:
 *
 * ```js
 * import {
 *   useAreFeatureFlagsLoading,
 *   useFeatureFlagEnabled
 * }from '@frameio/feature-flags';
 *
 *
 * const areFlagsLoading = useAreFeatureFlagsLoading();
 * const isMyFlagEnabled = useFeatureFlagEnabled('v4.my_flag');
 * const showFeature = !areFlagsLoading && isMyFlagEnabled;
 * ```
 *
 * Example for switching between two states, a default and flagged feature:
 *
 * ```js
 * import {
 *   useHaveFeatureFlagsLoaded,
 *   useFeatureFlagEnabled
 * }from '@frameio/feature-flags';
 *
 * const haveFlagsLoaded = useHaveFeatureFlagsLoaded();
 * const isMyFlagEnabled = useFeatureFlagEnabled('v4.my_flag');
 *
 * return (
 *  <>
 *    {haveFlagsLoaded && isMyFlagEnabled ? <MyFeature /> : <DefaultFeature />}
 *  </>
 * )
 * ```
 */
export const FeatureFlagsContextProvider = ({
  accountId,
  children,
  mode,
  shareId,
}: FeatureFlagsContextProviderProps) => {
  const variablesForAccount: GetFeatureFlagsQueryVariables = {
    accountId,
    flagNames,
  };

  /**
   * Secure Reviewers (aka Inbox-only users) don't have access to `account`,
   * so we need to get the Feature flag data from the AuthenticatedUser.
   * */
  const {
    data: dataForSecureReviewer,
    error: errorForSecureReviewer,
    loading: loadingForSecureReviewer,
  } = useGetFeatureFlagsForSecureReviewerQuery({
    skip: mode !== 'secure-reviewer' || !accountId,
    variables: {
      accountId,
      flagNames,
    },
  });

  // This will skip on /shares routes, where `mode` prop is set to "share-review".
  // The `account.featureFlags` API will check each flag's gate *first* against
  // the User ID (and percentage rollout as a hashed check on the User ID). If
  // disabled for the user, the API *also* checks against the Account ID (and
  // percentage rollout a second time against the Account ID).
  const {
    data: dataForAccount,
    error: errorForAccount,
    loading: loadingForAccount,
  } = useGetFeatureFlagsQuery({
    variables: variablesForAccount,
    skip: mode !== 'app' || !accountId,
  });

  // For /shares, we'll fetch the feature flags from the share.featureFlags API
  // on the server-side and hydrate the client cache from `layout.tsx`. We can
  // then retrieve the flags data from the cache using the `useFragment` hook.
  // No feature flags data will be retrievable until the share is authenticated
  // (including via passphrase if applicable).
  //
  // If the share is accessed by an authenticated User, the `share.featureFlags`
  // API will behave similarly to `account.featureFlags` (checking flag gating
  // first against auth'd User ID, then against Account ID derived as parent of
  // the Share's underlying Collection).
  //
  // If, however, the request is from a public Share without an authenticated
  // User, the API will *only* check flag states against the parent Account ID.
  const { data: dataForShare } = useFragment<ShareFeatureFlagsFragment>({
    fragment: ShareFeatureFlags,
    fragmentName: 'ShareFeatureFlags',
    from: {
      __typename: 'Share',
      id: shareId,
    },
    variables: {
      flagNames,
      shareId,
    },
  });

  const { data, loading, error } = getModeData(mode, {
    app: {
      data: dataForAccount,
      loading: loadingForAccount,
      error: errorForAccount,
    },
    share: { data: dataForShare },
    secureReviewer: {
      data: dataForSecureReviewer,
      loading: loadingForSecureReviewer,
      error: errorForSecureReviewer,
    },
  });

  // If we're in share mode, assume flag query has already run server-side, and
  // we're merely checking the cache via fragment above.
  const haveFeatureFlagsLoaded =
    mode === 'share-review' ? true : data !== undefined || Boolean(error);

  const [overrides, updateOverrides] = useFeatureFlagOverrides();

  const flags = useMemo(() => {
    const allFlags = new Map<FeatureFlagName, FeatureFlagState>(
      flagNames.map((name) => [
        name,
        {
          name,
          enabled: false,
        } as FeatureFlagState,
      ])
    );
    function get(flag: FeatureFlagName) {
      const existing = allFlags.get(flag);
      if (existing) return existing;
      const node = {
        name: flag,
        enabled: false,
      } as FeatureFlagState;
      allFlags.set(flag, node);
      return node;
    }
    for (const f of data?.featureFlags || []) {
      // eslint-disable-next-line no-continue
      if (!f?.name) continue;
      const name = f.name as FeatureFlagName;
      Object.assign(get(name), f);
    }
    for (const [name, override] of Object.entries(overrides ?? {})) {
      const flagState = get(name as FeatureFlagName);
      // eslint-disable-next-line no-continue
      if (!flagState || override == null) continue;
      flagState.override = true;
      flagState.originalValue = flagState.enabled;
      flagState.enabled = override;
    }
    return allFlags;
  }, [data?.featureFlags, overrides]);

  const isFeatureFlagEnabled = useCallback(
    (flagName: FeatureFlagName) => flags?.get(flagName)?.enabled ?? false,
    [flags]
  );

  const getAllFeatureFlagStates = useCallback(
    () => [...flags.values()] as FeatureFlagState[],
    [flags]
  );

  const value = useMemo(
    () => ({
      areFeatureFlagsLoading: loading,
      getAllFeatureFlagStates,
      haveFeatureFlagsLoaded,
      isFeatureFlagEnabled,
      updateOverrides,
    }),
    [
      getAllFeatureFlagStates,
      haveFeatureFlagsLoaded,
      isFeatureFlagEnabled,
      loading,
      updateOverrides,
    ]
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/***************** SELECTORS */

export const useAreFeatureFlagsLoading = () =>
  useContextSelector(
    FeatureFlagsContext,
    (value) => value?.areFeatureFlagsLoading
  );

export const useGetAllFeatureFlagStates = () => {
  return useContextSelector(
    FeatureFlagsContext,
    (value) => value?.getAllFeatureFlagStates()
  );
};

export const useUpdateFeatureFlagOverrides = () => {
  return useContextSelector(
    FeatureFlagsContext,
    (value) => value?.updateOverrides
  );
};

export const useHaveFeatureFlagsLoaded = () =>
  useContextSelector(
    FeatureFlagsContext,
    (value) => value?.haveFeatureFlagsLoaded
  );

export const useFeatureFlagEnabled = (flagName: FeatureFlagName) =>
  useContextSelector(
    FeatureFlagsContext,
    (value) => value?.isFeatureFlagEnabled(flagName)
  );

function useFeatureFlagOverrides() {
  return useStorageCell<Record<FeatureFlagName, boolean>>(
    'reactive-map-featureFlagOverridesMap'
  );
}
